import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotification } from "../context/NotificationContext";
import { useTranslation } from "react-i18next";

const MetodoPagoFormPage = () => {
  const { metodo } = useParams();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { t } = useTranslation();

  const [form, setForm] = useState({
    billing_name: "",
    billing_address: "",
    billing_country: "",
    billing_email: "",
    billing_phone: "",
    tax_id: "",
    wallet_address: "",
    blockchain_network: "ethereum",
    iban: "",
    paypal_email: "",
  });

  const [tokenBalance, setTokenBalance] = useState(0);
  const [tokensToWithdraw, setTokensToWithdraw] = useState(0);
  const [impressions, setImpressions] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showSummary, setShowSummary] = useState(false);
  const [error, setError] = useState(null);

  const IMPRESSIONS_PER_TOKEN = 10;
  const MIN_TOKENS_TO_WITHDRAW = 99;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const profileRes = await fetch("/api/monetizacion/profile/", {
          headers: { Authorization: `Bearer ${localStorage.getItem("access")}` },
        });
        const profileData = await profileRes.json();
        if (profileRes.ok) {
          setForm((prev) => ({ ...prev, ...profileData }));
          const available = parseFloat(profileData.tokens_acumulados || "0");
          setTokenBalance(available);
          setTokensToWithdraw(available >= MIN_TOKENS_TO_WITHDRAW ? available : 0);
        }

        const statsRes = await fetch("/api/ads/stats/", {
          headers: { Authorization: `Bearer ${localStorage.getItem("access")}` },
        });
        const statsData = await statsRes.json();
        if (statsRes.ok) {
          setImpressions(statsData.impressions || 0);
        }
      } catch (err) {
        console.error("Error al cargar datos de perfil o estadísticas", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const validateForm = () => {
    const { billing_name, billing_address, billing_country, billing_email, billing_phone } = form;

    if (!billing_name || !billing_address || !billing_country || !billing_email || !billing_phone) {
      return t("payment.errors.required_fields");
    }

    if (!/\S+@\S+\.\S+/.test(form.billing_email)) {
      return t("payment.errors.invalid_email");
    }

    if (tokensToWithdraw < MIN_TOKENS_TO_WITHDRAW) {
      return t("payment.errors.min_tokens", {
        min: MIN_TOKENS_TO_WITHDRAW,
        impressions: MIN_TOKENS_TO_WITHDRAW * IMPRESSIONS_PER_TOKEN,
      });
    }

    switch (metodo) {
      case "paypal":
        if (!form.paypal_email || !form.paypal_email.includes("@")) {
          return t("payment.errors.paypal_required");
        }
        break;
      case "iban":
        if (!/^([A-Z]{2}[0-9]{2}[A-Z0-9]{1,30})$/.test(form.iban)) {
          return t("payment.errors.iban_invalid");
        }
        break;
      case "blockchain":
        if (!form.wallet_address) return t("payment.errors.wallet_required");
        break;
    }

    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    const validationError = validateForm();
    if (validationError) return setError(validationError);
    setShowSummary(true);
  };

  const handleConfirm = async () => {
    try {
      const profilePayload = {
        payment_method: metodo,
        billing_name: form.billing_name,
        billing_address: form.billing_address,
        billing_country: form.billing_country,
        billing_email: form.billing_email,
        billing_phone: form.billing_phone,
        tax_id: form.tax_id,
        is_ready_for_payment: true,
      };

      if (metodo === "paypal") profilePayload.paypal_email = form.paypal_email;
      if (metodo === "iban") profilePayload.iban = form.iban;
      if (metodo === "blockchain") {
        profilePayload.wallet_address = form.wallet_address;
        profilePayload.blockchain_network = form.blockchain_network;
      }

      const profileRes = await fetch("/api/monetizacion/profile/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
        body: JSON.stringify(profilePayload),
      });

      if (!profileRes.ok) {
        notify("❌ " + t("payment.errors.save_failed"), "error");
        return;
      }

      const withdrawalPayload = {
        method: metodo,
        tokens: tokensToWithdraw,
        billing_name: form.billing_name,
        billing_address: form.billing_address,
        billing_country: form.billing_country,
        billing_email: form.billing_email,
        billing_phone: form.billing_phone,
        tax_id: form.tax_id,
        iban: form.iban,
        paypal_email: form.paypal_email,
        wallet_address: form.wallet_address,
        blockchain_network: form.blockchain_network,
      };

      const retiroRes = await fetch("/api/monetizacion/withdrawals/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
        body: JSON.stringify(withdrawalPayload),
      });

      if (!retiroRes.ok) {
        notify(t("payment.errors.withdrawal_failed"), "warning");
      } else {
        notify(t("payment.success"), "success");
      }

      navigate("/monetizacion/ganancias");
    } catch (err) {
      console.error("Error al guardar método o retiro", err);
      notify("❌ " + t("payment.errors.connection"), "error");
    }
  };

  const handleCancel = () => navigate("/monetizacion/ganancias");

  if (isLoading) return <p className="p-4 mt-10">{t("payment.loading")}</p>;

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-xl shadow mt-5">
      <h2 className="text-2xl font-bold mb-4 text-center">
        {showSummary
          ? t("payment.confirmation_title")
          : `${t("payment.method_label")} ${metodo.toUpperCase()}`}
      </h2>

      <div className="mb-4 text-center text-sm text-gray-700">
        {t("payment.available_tokens")}: <strong>{tokenBalance}</strong>
      </div>

      {error && <p className="text-red-600 text-center mb-4">{error}</p>}

      {!showSummary ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium">{t("payment.withdraw_amount")}</label>
            <input
              type="range"
              min={MIN_TOKENS_TO_WITHDRAW}
              max={tokenBalance}
              step={0.1}
              value={tokensToWithdraw}
              onChange={(e) => setTokensToWithdraw(Number(e.target.value))}
              className="w-full mt-1"
            />
            <div className="text-center mt-1 text-sm font-semibold">
              {t("payment.tokens_label", {
                count: tokensToWithdraw,
                euros: (tokensToWithdraw * 0.01).toFixed(2),
              })}
            </div>
          </div>

          {["billing_name", "billing_address", "billing_country", "billing_email", "billing_phone", "tax_id"].map((field) => (
            <div key={field}>
              <label className="block text-sm font-medium">{t(`payment.fields.${field}`)}</label>
              <input
                name={field}
                value={form[field]}
                onChange={handleChange}
                required={field !== "tax_id"}
                className="w-full border rounded px-3 py-2"
                type={field.includes("email") ? "email" : "text"}
              />
            </div>
          ))}

          {metodo === "paypal" && (
            <div>
              <label className="block text-sm font-medium">{t("payment.fields.paypal_email")}</label>
              <input name="paypal_email" type="email" value={form.paypal_email} onChange={handleChange} required className="w-full border rounded px-3 py-2" />
            </div>
          )}
          {metodo === "iban" && (
            <div>
              <label className="block text-sm font-medium">{t("payment.fields.iban")}</label>
              <input name="iban" value={form.iban} onChange={handleChange} required className="w-full border rounded px-3 py-2" />
            </div>
          )}
          {metodo === "blockchain" && (
            <>
              <div>
                <label className="block text-sm font-medium">{t("payment.fields.wallet_address")}</label>
                <input name="wallet_address" value={form.wallet_address} onChange={handleChange} required className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium">{t("payment.fields.blockchain_network")}</label>
                <select name="blockchain_network" value={form.blockchain_network} onChange={handleChange} className="w-full border rounded px-3 py-2">
                  <option value="ethereum">{t("payment.blockchain_options.ethereum")}</option>
                  <option value="bitcoin">{t("payment.blockchain_options.bitcoin")}</option>
                  <option value="polygon">{t("payment.blockchain_options.polygon")}</option>
                  <option value="otro">{t("payment.blockchain_options.otro")}</option>
                </select>
              </div>
            </>
          )}

          <div className="flex justify-between pt-4">
            <button type="button" onClick={handleCancel} className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400">
              {t("payment.buttons.cancel")}
            </button>
            <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700">
              {t("payment.buttons.save_continue")}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-2 text-left">
          <p><strong>{t("payment.fields.billing_name")}:</strong> {form.billing_name}</p>
          <p><strong>{t("payment.fields.billing_address")}:</strong> {form.billing_address}</p>
          <p><strong>{t("payment.fields.billing_country")}:</strong> {form.billing_country}</p>
          <p><strong>{t("payment.fields.billing_email")}:</strong> {form.billing_email}</p>
          <p><strong>{t("payment.fields.billing_phone")}:</strong> {form.billing_phone}</p>
          {form.tax_id && <p><strong>{t("payment.fields.tax_id")}:</strong> {form.tax_id}</p>}
          {metodo === "paypal" && <p><strong>{t("payment.fields.paypal_email")}:</strong> {form.paypal_email}</p>}
          {metodo === "iban" && <p><strong>{t("payment.fields.iban")}:</strong> {form.iban}</p>}
          {metodo === "blockchain" && (
            <>
              <p><strong>{t("payment.fields.wallet_address")}:</strong> {form.wallet_address}</p>
              <p><strong>{t("payment.fields.blockchain_network")}:</strong> {form.blockchain_network}</p>
            </>
          )}
          <p><strong>{t("payment.summary.requested")}:</strong> {tokensToWithdraw} tokens (~{(tokensToWithdraw * 0.01).toFixed(2)} €)</p>

          <div className="flex justify-between pt-4">
            <button onClick={() => setShowSummary(false)} className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400">
              {t("payment.buttons.edit")}
            </button>
            <button onClick={handleConfirm} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700">
              {t("payment.buttons.confirm_save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetodoPagoFormPage;
